import { getSymplouGenerativeModel } from "./firebase-ai";

export type CategorySupplierAiSuggestion = {
  category: {
    mode: "existing" | "new" | "none";
    existingId?: string | null;
    newTitle?: string | null;
  };
  supplier: {
    mode: "existing" | "new" | "none";
    existingId?: string | null;
    newTitle?: string | null;
  };
};

function extractJson(text: string): unknown {
  const trimmed = text.trim();
  const block = /^```(?:json)?\s*([\s\S]*?)```/m.exec(trimmed);
  const jsonStr = block ? block[1].trim() : trimmed;
  return JSON.parse(jsonStr);
}

function normalizeSuggestion(raw: unknown): CategorySupplierAiSuggestion | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const cat = o.category;
  const sup = o.supplier;
  if (!cat || !sup || typeof cat !== "object" || typeof sup !== "object") return null;
  const c = cat as Record<string, unknown>;
  const s = sup as Record<string, unknown>;
  const cm = c.mode === "existing" || c.mode === "new" || c.mode === "none" ? c.mode : "none";
  const sm = s.mode === "existing" || s.mode === "new" || s.mode === "none" ? s.mode : "none";
  return {
    category: {
      mode: cm,
      existingId: typeof c.existingId === "string" ? c.existingId : null,
      newTitle: typeof c.newTitle === "string" ? c.newTitle : null,
    },
    supplier: {
      mode: sm,
      existingId: typeof s.existingId === "string" ? s.existingId : null,
      newTitle: typeof s.newTitle === "string" ? s.newTitle : null,
    },
  };
}

/**
 * Sugere categoria e fornecedor com base no nome do produto e nas listas cadastradas.
 */
export async function suggestCategoryAndSupplier(payload: {
  productName: string;
  categories: Array<{ id: string; title: string }>;
  suppliers: Array<{ id: string; title: string }>;
}): Promise<CategorySupplierAiSuggestion | null> {
  const name = payload.productName.trim();
  if (name.length < 3) return null;

  const catLines = payload.categories.map((c) => `- id "${c.id}" — ${c.title}`).join("\n");
  const supLines = payload.suppliers.map((s) => `- id "${s.id}" — ${s.title}`).join("\n");

  const model = getSymplouGenerativeModel();
  const prompt = `Você ajuda no cadastro de produtos de varejo no Brasil (português).

Nome do produto: "${name}"

Categorias existentes (use existingId exatamente como abaixo se couber; senão sugira categoria nova):
${catLines || "(nenhuma)"}

Fornecedores existentes:
${supLines || "(nenhum)"}

Responda APENAS um JSON válido, sem markdown, neste formato exato:
{
  "category": { "mode": "existing"|"new"|"none", "existingId": string|null, "newTitle": string|null },
  "supplier": { "mode": "existing"|"new"|"none", "existingId": string|null, "newTitle": string|null }
}

Regras:
- mode "existing" só se houver id listado acima que faça sentido; preencha existingId.
- mode "new" se precisar de categoria ou fornecedor que não exista na lista; preencha newTitle (nome curto).
- mode "none" se não tiver sugestão segura para aquele campo.
- Não invente ids que não estão na lista para mode existing.`;

  const result = await model.generateContent(prompt);
  const text = result.response.text();
  try {
    return normalizeSuggestion(extractJson(text));
  } catch {
    return null;
  }
}
